import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, Search, Download, Camera, Trash2, Save, X, GitBranch, Eye, Upload, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import api, { fileUrl, getToken, API } from "@/lib/api";
import StatusBadge, { STATUS_FLOW } from "@/components/StatusBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const MARCAS = ["Natura", "O Boticário", "Avon", "Eudora", "Quem Disse Berenice", "Wepink", "Outras"];
const UFS = ["AC","AL","AM","AP","BA","CE","DF","ES","GO","MA","MG","MS","MT","PA","PB","PE","PI","PR","RJ","RN","RO","RR","RS","SC","SE","SP","TO"];
const TIPOS = ["Avaria", "Falta", "Divergência", "Vazamento", "Rótulo", "Outro"];
const CATEGORIAS = ["Transporte", "Armazenagem", "Separação", "Recebimento"];

const EMPTY_FORM = { tipo: "Avaria", loja: "", marca: "Natura", uf: "SP", data: new Date().toISOString().slice(0, 10), produtos: "", motivo: "", categoria: "Transporte" };

export default function Ocorrencias() {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [fStatus, setFStatus] = useState("");
  const [fUf, setFUf] = useState("");
  const [fMarca, setFMarca] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [foto, setFoto] = useState(null);
  const [fotoPreview, setFotoPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  const [detail, setDetail] = useState(null);
  const [detailStatus, setDetailStatus] = useState("");
  const [causaOpen, setCausaOpen] = useState(false);
  const [causa, setCausa] = useState(null);
  const [importing, setImporting] = useState(false);
  const [clearing, setClearing] = useState(false);
  const fileInputRef = useRef(null);

  const onImportDevolucoes = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/ocorrencias/devolucoes/import", fd);
      toast.success(`Devoluções importadas · ${data.inseridos} novas, ${data.atualizados} atualizadas`);
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao importar devoluções");
    } finally {
      setImporting(false);
    }
  };

  const clearOcorrencias = async () => {
    if (!window.confirm("Remover TODAS as ocorrências? Esta ação também apaga FACs manuais e dados demonstrativos.")) return;
    setClearing(true);
    try {
      const { data } = await api.post("/ocorrencias/reset");
      toast.success(`${data.removidos} ocorrência(s) removida(s)`);
      fetchItems();
    } catch {
      toast.error("Erro ao limpar ocorrências");
    } finally {
      setClearing(false);
    }
  };

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/ocorrencias", { params: { q: q || undefined, status: fStatus || undefined, uf: fUf || undefined, marca: fMarca || undefined } });
      setItems(res.data);
    } catch {
      toast.error("Erro ao carregar ocorrências");
    } finally {
      setLoading(false);
    }
  }, [q, fStatus, fUf, fMarca]);

  useEffect(() => { fetchItems(); }, [fetchItems]);
  useEffect(() => { setPage(1); }, [q, fStatus, fUf, fMarca]);

  useEffect(() => {
    if (params.get("novo") === "1") {
      setCreateOpen(true);
      setParams({}, { replace: true });
    }
  }, [params, setParams]);

  const paged = useMemo(() => items.slice((page - 1) * pageSize, page * pageSize), [items, page, pageSize]);
  const totalPages = Math.max(1, Math.ceil(items.length / pageSize));

  const onFotoChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) { toast.error("Foto maior que 10MB"); return; }
    setFoto(f);
    const reader = new FileReader();
    reader.onload = () => setFotoPreview(reader.result);
    reader.readAsDataURL(f);
  };

  const createOcorrencia = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      let foto_path = null;
      if (foto) {
        const fd = new FormData();
        fd.append("file", foto);
        const up = await api.post("/files/upload", fd);
        foto_path = up.data.path;
      }
      await api.post("/ocorrencias", { ...form, foto_path });
      toast.success("Ocorrência registrada com sucesso");
      setCreateOpen(false);
      setForm(EMPTY_FORM);
      setFoto(null); setFotoPreview(null);
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao registrar");
    } finally {
      setSaving(false);
    }
  };

  const openDetail = async (id) => {
    const res = await api.get(`/ocorrencias/${id}`);
    setDetail(res.data);
    setDetailStatus(res.data.status);
  };

  const saveStatus = async () => {
    try {
      await api.patch(`/ocorrencias/${detail.id}`, { status: detailStatus });
      toast.success("Status atualizado");
      setDetail(null);
      fetchItems();
    } catch { toast.error("Erro ao atualizar status"); }
  };

  const deleteOcc = async () => {
    try {
      await api.delete(`/ocorrencias/${detail.id}`);
      toast.success("Ocorrência excluída");
      setDetail(null);
      fetchItems();
    } catch { toast.error("Erro ao excluir"); }
  };

  const exportCsv = async () => {
    const res = await api.get("/ocorrencias/export/csv", { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = "ocorrencias.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const openCausa = async () => {
    const res = await api.get("/ocorrencias/causa-raiz");
    setCausa(res.data);
    setCausaOpen(true);
  };

  const inputCls = "w-full bg-white border border-[#D9E3DC] rounded-md px-3 py-2 text-sm text-[#0F2A1F] focus:outline-none focus:ring-2 focus:ring-[#0F6B3A] placeholder:text-[#8A9A90]";

  return (
    <div className="space-y-5" data-testid="ocorrencias-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#0F2A1F]">Gestão de Ocorrências</h1>
          <p className="text-sm text-[#5A6B62] mt-1">Fichas de Análise de Conformidade (FAC)</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <input ref={fileInputRef} type="file" accept=".xlsx" className="hidden" onChange={onImportDevolucoes} data-testid="import-devolucoes-input" />
          <button data-testid="import-devolucoes-btn" onClick={() => fileInputRef.current?.click()} disabled={importing}
            className="bg-white hover:bg-[#F1F5F2] text-[#0F2A1F] border border-[#D9E3DC] font-medium px-4 py-2.5 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-60">
            <Upload className="w-4 h-4" /> {importing ? "Importando..." : "Importar Devoluções"}
          </button>
          <button data-testid="limpar-ocorrencias-btn" onClick={clearOcorrencias} disabled={clearing}
            className="bg-white hover:bg-red-50 text-red-700 border border-red-200 font-medium px-4 py-2.5 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-60">
            <RotateCcw className="w-4 h-4" /> {clearing ? "Limpando..." : "Limpar Ocorrências"}
          </button>
          <button data-testid="causa-raiz-btn" onClick={openCausa}
            className="bg-white hover:bg-[#F1F5F2] text-[#0F2A1F] border border-[#D9E3DC] font-medium px-4 py-2.5 rounded-lg transition-colors flex items-center gap-2">
            <GitBranch className="w-4 h-4" /> Causa-Raiz
          </button>
          <button data-testid="nova-ocorrencia-btn" onClick={() => setCreateOpen(true)}
            className="bg-[#2DC26B] hover:bg-[#22A85B] text-white font-semibold px-4 py-2.5 rounded-lg shadow-sm flex items-center gap-2 transition-colors">
            <Plus className="w-4 h-4" /> Nova Ocorrência
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white border border-[#D9E3DC] rounded-lg shadow-sm p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        <div className="relative lg:col-span-2">
          <Search className="w-4 h-4 text-[#8A9A90] absolute left-3 top-1/2 -translate-y-1/2" />
          <input data-testid="filtro-busca-input" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por FAC, produto, motivo, loja..." className={`${inputCls} pl-9`} />
        </div>
        <select data-testid="filtro-status-select" value={fStatus} onChange={(e) => setFStatus(e.target.value)} className={inputCls}>
          <option value="">Todos os status</option>
          {STATUS_FLOW.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select data-testid="filtro-uf-select" value={fUf} onChange={(e) => setFUf(e.target.value)} className={inputCls}>
          <option value="">Todas as UFs</option>
          {UFS.map((u) => <option key={u} value={u}>{u}</option>)}
        </select>
        <div className="flex gap-2">
          <select data-testid="filtro-marca-select" value={fMarca} onChange={(e) => setFMarca(e.target.value)} className={inputCls}>
            <option value="">Todas as marcas</option>
            {MARCAS.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          <button data-testid="export-csv-btn" onClick={exportCsv} title="Exportar CSV"
            className="shrink-0 bg-white hover:bg-[#F1F5F2] text-[#0F2A1F] border border-[#D9E3DC] rounded-md px-3 transition-colors">
            <Download className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Tabela */}
      <div className="bg-white border border-[#D9E3DC] rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full table-fixed text-sm" data-testid="ocorrencias-table">
            <thead>
              <tr className="bg-[#F4F8F5] border-b border-[#D9E3DC] text-xs font-bold text-[#0F2A1F] uppercase tracking-wider">
                <th className="w-[92px] sm:w-auto text-left py-3 px-2 sm:px-4">FAC #</th>
                <th className="w-[72px] sm:w-auto text-left py-3 px-2 sm:px-4">Tipo</th>
                <th className="text-left py-3 px-2 sm:px-4">Loja</th>
                <th className="hidden sm:table-cell text-left py-3 px-4">Marca</th>
                <th className="hidden sm:table-cell text-left py-3 px-4">UF</th>
                <th className="hidden sm:table-cell text-left py-3 px-4">Motivo</th>
                <th className="hidden sm:table-cell text-left py-3 px-4">Status</th>
                <th className="hidden sm:table-cell text-left py-3 px-4">Data</th>
                <th className="w-12 sm:w-auto text-left py-3 px-2 sm:px-4">
                  <span className="sr-only sm:not-sr-only">Ações</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="py-10 text-center text-[#8A9A90]">Carregando...</td></tr>
              ) : paged.length === 0 ? (
                <tr><td colSpan={9} className="py-10 text-center text-[#8A9A90]" data-testid="ocorrencias-empty">Nenhuma ocorrência encontrada</td></tr>
              ) : paged.map((o) => (
                <tr key={o.id} className="border-b border-[#F1F5F2] hover:bg-[#F4F8F5]/80 transition-colors" data-testid={`ocorrencia-row-${o.fac}`}>
                  <td className="py-3 px-2 sm:px-4 font-mono text-xs font-semibold text-[#0F2A1F] truncate">{o.fac}</td>
                  <td className="py-3 px-2 sm:px-4 text-[#5A6B62] truncate">{o.tipo}</td>
                  <td className="py-3 px-2 sm:px-4 text-[#5A6B62] truncate">{o.loja}</td>
                  <td className="hidden sm:table-cell py-3 px-4 text-[#5A6B62]">{o.marca}</td>
                  <td className="hidden sm:table-cell py-3 px-4 text-[#5A6B62]">{o.uf}</td>
                  <td className="hidden sm:table-cell py-3 px-4 text-[#5A6B62] max-w-[180px] truncate" title={o.motivo}>{o.motivo}</td>
                  <td className="hidden sm:table-cell py-3 px-4"><StatusBadge status={o.status} /></td>
                  <td className="hidden sm:table-cell py-3 px-4 text-[#8A9A90] text-xs whitespace-nowrap">{o.data}</td>
                  <td className="py-3 px-2 sm:px-4">
                    <button data-testid={`ver-detalhes-${o.fac}`} onClick={() => openDetail(o.id)}
                      className="text-[#0F6B3A] hover:bg-[#EAF7EF] p-1.5 rounded-md transition-colors" title="Ver detalhes">
                      <Eye className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 border-t border-[#F1F5F2] flex flex-col sm:flex-row items-center justify-between gap-3">
          <span className="text-xs text-[#8A9A90]" data-testid="pagination-info">{items.length} ocorrência(s) · página {page} de {totalPages}</span>
          <div className="flex items-center gap-2">
            <select data-testid="page-size-select" value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
              className="border border-[#D9E3DC] rounded-md px-2 py-1.5 text-xs text-[#0F2A1F]">
              {[10, 25, 50].map((n) => <option key={n} value={n}>{n}/página</option>)}
            </select>
            <button data-testid="page-prev-btn" disabled={page <= 1} onClick={() => setPage(page - 1)}
              className="border border-[#D9E3DC] rounded-md px-3 py-1.5 text-xs font-medium text-[#0F2A1F] disabled:opacity-40 hover:bg-[#F1F5F2]">Anterior</button>
            <button data-testid="page-next-btn" disabled={page >= totalPages} onClick={() => setPage(page + 1)}
              className="border border-[#D9E3DC] rounded-md px-3 py-1.5 text-xs font-medium text-[#0F2A1F] disabled:opacity-40 hover:bg-[#F1F5F2]">Próxima</button>
          </div>
        </div>
      </div>

      {/* Modal: Nova Ocorrência */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="modal-nova-ocorrencia">
          <DialogHeader>
            <DialogTitle className="text-[#0F2A1F]">Registro de FAC — Ficha de Análise de Conformidade</DialogTitle>
          </DialogHeader>
          <form onSubmit={createOcorrencia} className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
            <div>
              <label className="text-xs font-semibold text-[#0F2A1F]">Tipo</label>
              <select data-testid="form-tipo" className={`${inputCls} mt-1`} value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })}>
                {TIPOS.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-[#0F2A1F]">Loja</label>
              <input data-testid="form-loja" required className={`${inputCls} mt-1`} value={form.loja} onChange={(e) => setForm({ ...form, loja: e.target.value })} placeholder="Ex: Loja BSC" />
            </div>
            <div>
              <label className="text-xs font-semibold text-[#0F2A1F]">Marca</label>
              <select data-testid="form-marca" className={`${inputCls} mt-1`} value={form.marca} onChange={(e) => setForm({ ...form, marca: e.target.value })}>
                {MARCAS.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-[#0F2A1F]">UF</label>
              <select data-testid="form-uf" className={`${inputCls} mt-1`} value={form.uf} onChange={(e) => setForm({ ...form, uf: e.target.value })}>
                {UFS.map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-[#0F2A1F]">Data</label>
              <input data-testid="form-data" type="date" required className={`${inputCls} mt-1`} value={form.data} onChange={(e) => setForm({ ...form, data: e.target.value })} />
            </div>
            <div>
              <label className="text-xs font-semibold text-[#0F2A1F]">Categoria</label>
              <select data-testid="form-categoria" className={`${inputCls} mt-1`} value={form.categoria} onChange={(e) => setForm({ ...form, categoria: e.target.value })}>
                {CATEGORIAS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs font-semibold text-[#0F2A1F]">Produto(s) / SKU(s)</label>
              <input data-testid="form-produtos" required className={`${inputCls} mt-1`} value={form.produtos} onChange={(e) => setForm({ ...form, produtos: e.target.value })} placeholder="Ex: SKU-4821 — Eau de Parfum 75ml" />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs font-semibold text-[#0F2A1F]">Motivo da não-conformidade</label>
              <textarea data-testid="form-motivo" required rows={2} className={`${inputCls} mt-1`} value={form.motivo} onChange={(e) => setForm({ ...form, motivo: e.target.value })} placeholder="Ex: Frasco quebrado no transporte" />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs font-semibold text-[#0F2A1F]">Foto do defeito (opcional, máx 10MB)</label>
              <div className="mt-1 flex items-start gap-3">
                <label className="cursor-pointer bg-white hover:bg-[#F1F5F2] border border-dashed border-[#D9E3DC] rounded-md px-4 py-3 flex items-center gap-2 text-sm text-[#5A6B62] transition-colors">
                  <Camera className="w-4 h-4" /> {foto ? "Trocar foto" : "Anexar foto"}
                  <input data-testid="form-foto-input" type="file" accept="image/*" className="hidden" onChange={onFotoChange} />
                </label>
                {fotoPreview && (
                  <img data-testid="form-foto-preview" src={fotoPreview} alt="preview" className="h-20 w-20 object-cover rounded-md border border-[#D9E3DC]" />
                )}
              </div>
            </div>
            <div className="sm:col-span-2 flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setCreateOpen(false)}
                className="bg-white hover:bg-[#F1F5F2] text-[#0F2A1F] border border-[#D9E3DC] font-medium px-4 py-2 rounded-lg">Cancelar</button>
              <button data-testid="form-submit-btn" type="submit" disabled={saving}
                className="bg-[#2DC26B] hover:bg-[#22A85B] disabled:opacity-60 text-white font-semibold px-5 py-2 rounded-lg shadow-sm transition-colors">
                {saving ? "Salvando..." : "Registrar FAC"}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Modal: Detalhe */}
      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="modal-detalhe-ocorrencia">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="text-[#0F2A1F] font-mono">{detail.fac}</DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm mt-2">
                {[["Tipo", detail.tipo], ["Loja", detail.loja], ["Marca", detail.marca], ["UF", detail.uf],
                  ["Data", detail.data], ["Categoria", detail.categoria], ["Criado por", detail.created_by]].map(([k, v]) => (
                  <div key={k}>
                    <span className="text-xs text-[#8A9A90] uppercase tracking-wide">{k}</span>
                    <p className="font-medium text-[#0F2A1F]">{v}</p>
                  </div>
                ))}
                <div className="col-span-2">
                  <span className="text-xs text-[#8A9A90] uppercase tracking-wide">Produto(s)</span>
                  <p className="font-medium text-[#0F2A1F]">{detail.produtos}</p>
                </div>
                <div className="col-span-2">
                  <span className="text-xs text-[#8A9A90] uppercase tracking-wide">Motivo</span>
                  <p className="font-medium text-[#0F2A1F]">{detail.motivo}</p>
                </div>
                {detail.foto_path && (
                  <div className="col-span-2">
                    <span className="text-xs text-[#8A9A90] uppercase tracking-wide">Foto do defeito</span>
                    <img data-testid="detalhe-foto" src={fileUrl(detail.foto_path)} alt="defeito"
                      className="mt-1 max-h-48 rounded-md border border-[#D9E3DC] object-contain bg-[#F4F8F5]" />
                  </div>
                )}
                <div className="col-span-2">
                  <span className="text-xs text-[#8A9A90] uppercase tracking-wide">Status do fluxo</span>
                  <select data-testid="detalhe-status-select" className={`${inputCls} mt-1`} value={detailStatus} onChange={(e) => setDetailStatus(e.target.value)}>
                    {STATUS_FLOW.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {STATUS_FLOW.map((s, i) => (
                      <span key={s} className={`text-[10px] px-1.5 py-0.5 rounded ${STATUS_FLOW.indexOf(detailStatus) >= i ? "bg-[#0F6B3A] text-white" : "bg-[#F1F5F2] text-[#8A9A90]"}`}>
                        {i + 1}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex justify-between gap-2 pt-4">
                <button data-testid="detalhe-excluir-btn" onClick={deleteOcc}
                  className="bg-white hover:bg-[#FEF2F2] text-[#B91C1C] border border-[#FECACA] font-medium px-4 py-2 rounded-lg flex items-center gap-2 transition-colors">
                  <Trash2 className="w-4 h-4" /> Excluir
                </button>
                <button data-testid="detalhe-salvar-btn" onClick={saveStatus}
                  className="bg-[#0F6B3A] hover:bg-[#0B5530] text-white font-medium px-5 py-2 rounded-lg shadow-sm flex items-center gap-2 transition-colors">
                  <Save className="w-4 h-4" /> Salvar status
                </button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal: Causa-Raiz */}
      <Dialog open={causaOpen} onOpenChange={setCausaOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="modal-causa-raiz">
          <DialogHeader>
            <DialogTitle className="text-[#0F2A1F]">Análise de Causa-Raiz</DialogTitle>
          </DialogHeader>
          {causa && (
            <div className="space-y-6 mt-2">
              <div>
                <h3 className="text-sm font-semibold text-[#0F2A1F] mb-3">Top 10 Motivos de Não-Conformidade</h3>
                <div className="space-y-2" data-testid="causa-top-motivos">
                  {causa.top_motivos.map((m, i) => {
                    const max = causa.top_motivos[0]?.total || 1;
                    return (
                      <div key={i} className="flex items-center gap-3">
                        <span className="text-xs text-[#5A6B62] w-48 truncate" title={m.motivo}>{m.motivo}</span>
                        <div className="flex-1 h-5 bg-[#F1F5F2] rounded-full overflow-hidden">
                          <div className="h-full bg-[#0F6B3A] rounded-full" style={{ width: `${(m.total / max) * 100}%` }} />
                        </div>
                        <span className="text-xs font-bold text-[#0F2A1F] w-8 text-right">{m.total}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-[#0F2A1F] mb-3">Distribuição por Categoria</h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="causa-categorias">
                  {causa.categorias.map((c) => (
                    <div key={c.categoria} className="bg-[#F4F8F5] border border-[#D9E3DC] rounded-lg p-3 text-center">
                      <p className="text-xl font-extrabold text-[#0F2A1F]">{c.total}</p>
                      <p className="text-xs text-[#5A6B62] mt-1">{c.categoria}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
